import Input from "@/components/common/Fields/Input";
import { firstTimeResetPasswordGenerateOTP } from "@/service/login.service";
import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import PasswordResetOTPModal from "./PasswordResetOTPModal";
import { handleAxiosError } from "@/lib/errorHandler";

function buildPasswordValidationRules(password) {
  return {
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    minLength: password.length >= 8,
    maxLength: password.length >= 8 && password.length <= 15,
    consecutive: password && !/(.)\1+/.test(password),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
}

const PASSWORD_VALIDATION_RULES = [
  { key: "lowercase", label: "At least one lowercase letter" },
  { key: "minLength", label: "Minimum 8 characters" },
  { key: "maxLength", label: "Maximum 15 characters" },
  { key: "uppercase", label: "At least one uppercase letter" },
  { key: "number", label: "At least one number" },
  { key: "consecutive", label: "No more than 2 consecutive characters" },
  { key: "specialChar", label: "At least one special character" },
];

function PasswordValidationRulesList({ rules }) {
  return (
    <ul className="text-sm space-y-1">
      {PASSWORD_VALIDATION_RULES.map((rule) => (
        <li
          key={rule.key}
          className={rules[rule.key] ? "text-green-600" : "text-gray-500"}
        >
          <span className={rules[rule.key] ? "" : "text-red-500"}>
            {rules[rule.key] ? "✔" : "✖"}
          </span>{" "}
          {rule.label}
        </li>
      ))}
    </ul>
  );
}

/**
 * Modal component to handle password expiration by verifying OTP and setting a new password.
 *
 * @component
 * @param {Object} props - React props
 * @param {boolean} props.isOpen - Flag to control the modal visibility
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.userName - Email or userName associated with the account
 * @param {string} props.password - Current (expired) password
 * @returns {JSX.Element|null}
 */
function PasswordResetModal({ isOpen, onClose, userName, password }) {
  const [isPasswordResetModalVisible, setIsPasswordResetModalVisible] =
    useState(true);
  const [currentPassword] = useState(password);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newApin, setNewApin] = useState("");
  const [showApin, setShowApin] = useState(true);
  const [confirmApin, setConfirmApin] = useState("");
  const [showConfirmApin, setShowConfirmApin] = useState(true);
  const [apinError, setApinError] = useState("");
  const [isPasswordResetOTPModalVisible, setIsPasswordResetOTPModalVisible] =
    useState(false);
  /**
   * Handles form submission to verify OTP and change password.
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    changePassword();
  };

  /**
   * Calls API to update the reset the password.
   *
   */
  const changePassword = async () => {
    if (!validateApin()) {
      return;
    }
    try {
      await firstTimeResetPasswordGenerateOTP({
        userName: userName?.toUpperCase(),
        currentPassword,
        newPassword,
        confirmPassword,
        newApin,
        confirmApin,
      });
      // onClose();
      setIsPasswordResetModalVisible(false);
      setIsPasswordResetOTPModalVisible(true);
    } catch (error) {
      handleAxiosError(error);
    }
  };

  const handleApinChange = (e) => {
    setNewApin(e.target.value);
    setApinError("");
  };

  const handleConfirmApinChange = (e) => {
    setConfirmApin(e.target.value);
    setApinError("");
  };

  const validateApin = () => {
    if (!newApin) {
      setApinError("Please Enter APIN");
      return false;
    }
    if (newApin.length !== 6) {
      setApinError("APIN must be exactly 6 digits");
      return false;
    }
    if (newApin !== confirmApin) {
      setApinError("APINs do not match. Please try again.");
      return false;
    }
    return true;
  };

  /** Toggles visibility of the new password field */
  const handleToggleNewPassword = () => setShowNewPassword((prev) => !prev);

  /** Toggles visibility of the confirm password field */
  const handleToggleConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);

  /**
   * On mount: resets states on unmount.
   */
  useEffect(() => {
    return () => {
      setNewPassword("");
      setShowNewPassword(false);
      setConfirmPassword("");
      setShowConfirmPassword(false);
    };
  }, []);

  // Validation rules for new password
  const validateNewPassword = buildPasswordValidationRules(newPassword);
  //Validation rules for confirm password
  const validateNewConfirmPassword = {
    isSame:
      newPassword.length !== 0 &&
      confirmPassword.length !== 0 &&
      newPassword === confirmPassword,
  };

  const isValidNewPassword = Object.values(validateNewPassword).every(Boolean);
  const isValidConfirmNewPassword = validateNewConfirmPassword.isSame;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 pt-20 lg:pt-20 md:pt-12 sm:pt-8 overflow-y-auto">
      {isPasswordResetModalVisible && (
        <div className="bg-white rounded-lg shadow-md w-[35%] p-6">
          {/* Title and Instructions */}
          <h2 className=" text-xl font-semibold mb-4">Reset Password</h2>
          <h2 className="lg:text-md font-bold mb-2">Verify Your Email</h2>

          {/* Password Change Form */}
          <form className="mt-4" onSubmit={handleSubmit}>
            {/* New Password Field */}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative mb-4">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                required
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:border-blue-400"
              />
              <button
                type="button"
                onClick={handleToggleNewPassword}
                className="absolute inset-y-0 right-2 flex items-center text-gray-600"
              >
                {showNewPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {/* Confirm Password Field */}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative mb-4">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                required
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Enter confirm password"
                className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:border-blue-400"
              />
              <button
                type="button"
                onClick={handleToggleConfirmPassword}
                className="absolute inset-y-0 right-2 flex items-center text-gray-600"
              >
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {/* Password Validation */}
            <PasswordValidationRulesList rules={validateNewPassword} />

            {/* Confirm Password Match */}
            <ul className="text-sm text-gray-600 space-y-1 mb-4">
              <li
                className={
                  validateNewConfirmPassword.isSame
                    ? "text-green-600"
                    : "text-gray-500"
                }
              >
                <span
                  className={
                    validateNewConfirmPassword.isSame ? "" : "text-red-500"
                  }
                >
                  {validateNewConfirmPassword.isSame ? "✔" : "✖"}
                </span>{" "}
                Passwords match
              </li>
            </ul>

            <div className="mb-6">
              <label
                htmlFor="newApin-input"
                className="block text-gray-700 font-semibold mb-2"
              >
                Enter new Apin
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <Input
                  type={showApin ? "numericPassword" : "numericPasswordText"}
                  placeholder="Enter new apin"
                  name="newApin"
                  value={newApin || ""}
                  onChange={handleApinChange}
                  autoComplete="new-password"
                  spellCheck={false}
                  minLength={6}
                  maxLength={6}
                  id="newApin-input"
                />

                <button
                  type="button"
                  onClick={() => setShowApin(!showApin)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showApin ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="confirm-apin-input"
                className="block text-gray-700 font-semibold mb-2"
              >
                Confirm APIN <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <Input
                  type={
                    showConfirmApin ? "numericPassword" : "numericPasswordText"
                  }
                  placeholder="Enter confirm apin"
                  name="confirmApin"
                  value={confirmApin || ""}
                  onChange={handleConfirmApinChange}
                  autoComplete="new-password1"
                  spellCheck={false}
                  minLength={6}
                  maxLength={6}
                  id="confirm-apin-input"
                />

                <button
                  type="button"
                  onClick={() => setShowConfirmApin(!showConfirmApin)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showConfirmApin ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            {apinError && <div className="text-red-600 mb-4">{apinError}</div>}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                disabled={
                  !isValidNewPassword ||
                  !isValidConfirmNewPassword ||
                  !newApin ||
                  !confirmApin
                }
                type="submit"
                className={`px-4 py-2 rounded text-white ${
                  isValidNewPassword &&
                  isValidConfirmNewPassword &&
                  newApin &&
                  confirmApin
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Reset password
              </button>
            </div>
          </form>
        </div>
      )}
      <PasswordResetOTPModal
        isOpen={isPasswordResetOTPModalVisible}
        onClose={onClose}
        userName={userName}
        currentPassword={password}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        newApin={newApin}
        confirmApin={confirmApin}
      />
    </div>
  );
}

export default PasswordResetModal;
